import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dots, FinishButton } from '@riddler-co-jp/specc-ui-components';
import * as React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useStatePersist as useState } from 'use-state-persist';

import {
  InstructionColor,
  InstructionLogo,
  InstructionText,
} from '../../../../lib/instructionSettings';
import TimeGauge from '../../../uiElements/timeGauge/TimeGauge';
import { titles } from '../../Words.json';
import Simulated from './Simulated.json';

interface Stage2AnswerProps {
  turn: number;
}

export type word = {
  id: number;
  word: string;
};

export type Item = {
  title: string;
  elements: number[][];
};

export const checkListWords: word[] = [
  { id: 0, word: '転用' },
  { id: 1, word: '応用' },
  { id: 2, word: '変更' },
  { id: 3, word: '拡大' },
  { id: 4, word: '縮小' },
  { id: 5, word: '代用' },
  { id: 6, word: '置換' },
  { id: 7, word: '逆転' },
  { id: 8, word: '統合' },
];

export const getStr: any = (id: number, key: number, onDelete: () => void) => {
  for (let i = 0; i < checkListWords.length; i++) {
    if (checkListWords[i].id == id) {
      return (
        <li key={`${id}-${key}`}>
          <p>{checkListWords[i].word}</p>
          {onDelete && (
            <div
              onClick={e => {
                if (onDelete) onDelete();
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </div>
          )}
        </li>
      );
    }
  }
  return null;
};

const Stage2Answer: React.FC<Stage2AnswerProps> = props => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = useLocation();
  const { trainingId } = useParams<'trainingId'>();
  const [resultSet, setResultSet] = useState<any>(
    `create-${trainingId}-resultSet`
  );
  const [focus, setFocus] = useState<number>(`create2-${trainingId}-focus`, -1); // 選択している要素
  const [tmpAns, setTmpAns] = useState<number[][]>(
    `create2-${trainingId}-tmpAns`,
    []
  );
  const [finished, setFinished] = useState<boolean>(
    `create2-${trainingId}-finished`,
    false
  );

  React.useEffect(() => {
    setFinished(false);
  }, [pathname]);

  const stage1Answer =
    resultSet?.stage1 == null ? [] : resultSet.stage1[props.turn].ideas;

  const dummy_elements: word[] = (
    stage1Answer.length > 0
      ? resultSet.stage1[props.turn].ideas
      : Simulated[props.turn].ideas
  ).map((single: string, i: number) => {
    return { id: i, word: single };
  });

  React.useEffect(() => {
    let tmp: number[][] = [];
    for (let i = 0; i < dummy_elements.length; i++) {
      tmp = [...tmp, []];
    }
    setTmpAns(tmp);
  }, []);

  const goTo = (url: string) => {
    window.setTimeout(() => {
      navigate(url);
    }, 0);
  };

  const changeFocus = (id: number) => {
    if (id === focus) setFocus(-1);
    else setFocus(id);
  };

  const addAns = (id: number) => {
    if (focus === -1) return;

    const tmp = [...tmpAns];
    tmp[focus].push(id);

    setTmpAns(tmp);
    setFocus(-1);
  };

  const removeAns = (id: number, t: number) => {
    tmpAns[id].splice(t, 1);
    setTmpAns([...tmpAns]);
  };

  const ElementsList = () => {
    if (tmpAns.length == 0) return null;
    return (
      <ElementsListWrapper>
        {dummy_elements.map((s: word, i) => {
          const selected = s.id === focus;
          const finished = tmpAns[s.id].length > 0;
          const disabled = focus != -1 && s.id != focus;

          return (
            <SingleElement
              key={i}
              style={{
                transform: selected ? 'scale(1.05)' : 'scale(1.0)',
                zIndex: selected ? '9' : 'initial',
                opacity: disabled ? '0.3' : '1.0',
                background: finished ? '#CED4DA' : 'white',
                transition: '0.2s !important',
                boxShadow: finished ? 'none' : '0 3px 6px rgba(0, 0, 0, 0.16)',
              }}
            >
              <div>
                <ElementBg onClick={() => changeFocus(s.id)} />
                {tmpAns[s.id].length > 0 && (
                  <i>
                    <Checked />
                  </i>
                )}
                <p
                  style={{
                    opacity: finished ? '0.3' : '1.0',
                  }}
                >
                  {s.word}
                </p>
              </div>
              <SelectedItems>
                {tmpAns[s.id].map((single, t) => {
                  return getStr(single, t, () => removeAns(s.id, t));
                })}
              </SelectedItems>
            </SingleElement>
          );
        })}
      </ElementsListWrapper>
    );
  };

  const CheckList = () => {
    const checkListSentences = [
      '他に使い道は\nないか？',
      '他のアイディアを\n借りられないか？',
      '変えられる\n部分はないか？',
      '大きくしてみたら\nどうか？',
      '小さくしてみたら\nどうか？',
      '他のもので代用\nできないか？',
      '入れ替えてみたら\nどうか？',
      '逆転してみたら\nどうか？',
      '組み合わせて\nみたらどうか？',
    ];
    return (
      <CheckListWrapper>
        {checkListWords.map((s, i) => {
          const clickable =
            focus != -1 && tmpAns[focus].includes(s.id) == false;
          return (
            <SingleCheckList
              key={i}
              onMouseDown={() => {
                if (clickable) addAns(s.id);
              }}
              clickable={clickable}
              style={
                focus == -1
                  ? {}
                  : { opacity: tmpAns[focus].includes(s.id) ? 0.4 : 1.0 }
              }
            >
              <p>{s.word}</p>
              <span>{checkListSentences[s.id]}</span>
            </SingleCheckList>
          );
        })}
      </CheckListWrapper>
    );
  };

  const next = () => {
    const sendData: Item = {
      title: titles[props.turn],
      elements: tmpAns,
    };

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const results: Item[] = resultSet?.stage2 ?? [];

    let _exist = false;
    results.map((single, i) => {
      if (single.title == sendData.title) {
        _exist = true;
        results[i] = sendData;
      }
    });

    if (_exist == false) {
      results.push(sendData);
    }

    const _resultSet = resultSet ?? [];
    _resultSet.stage2 = results;

    setTmpAns([]);
    setFinished(false);
    setFocus(-1);
    if (props.turn == 0) {
      setResultSet(_resultSet);
      goTo(`/${trainingId}/create2/countdown2`);
    } else {
      setResultSet(_resultSet);
      goTo(`/${trainingId}/create2/result`);
    }
  };

  return (
    <>
      <TimeGauge
        timerKey={trainingId + '-create2-' + props.turn}
        duration={600}
        stageName={InstructionText[2].title}
        logo={InstructionLogo}
        color={InstructionColor}
        onSkip={() => {
          next();
        }}
        onFinished={() => {
          setFinished(true);
        }}
        onBack={() => {
          navigate(`/${trainingId}/stages`);
        }}
      />
      <Wrap>
        <Wrapper>
          <Main>
            <ThemeWrap>
              <Theme>
                <h2>お題</h2>
                <div>
                  <p>{titles[props.turn]}</p>
                  <span>
                    <Dots
                      max={titles.length}
                      current={props.turn}
                      color={InstructionColor.primary}
                    />
                  </span>
                </div>
              </Theme>
              <Elements>
                <h2 style={{ opacity: focus == -1 ? '1.0' : '0.3' }}>
                  <TitleElement />
                </h2>
                <ElementsList />
              </Elements>
            </ThemeWrap>
          </Main>
          <Result>
            <TitleCheck />
            <CheckList />
          </Result>
        </Wrapper>
      </Wrap>
      <FinishButton
        color={InstructionColor}
        finished={finished}
        click={() => {
          next();
        }}
      >
        {props.turn == 0
          ? '次のお題に挑戦しましょう！'
          : '以上で実習は終了です。お疲れ様でした！'}
      </FinishButton>
    </>
  );
};

const ElementBg = styled.div`
  cursor: pointer;
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 10;
`;

export const SelectedItems = styled.ul`
  display: flex;

  li {
    z-index: 11;

    list-style: none;
    margin-top: 10px;
    margin-right: 10px;
    background: #f2a118;
    color: white;
    font-weight: bold;
    font-size: 1.6rem;
    padding: 3px 6px;
    display: flex;
    line-height: 2rem;

    > div:last-child {
      cursor: pointer;
      margin-left: 6px;
      font-size: 1.2rem;
      opacity: 0.6;

      &:hover {
        opacity: 1;
      }
    }
  }
`;

export const ElementsListWrapper = styled.ul``;

export const SingleElement = styled.li`
  display: block;
  position: relative;

  i {
    margin-right: 10px;
    margin-top: 6px;
  }

  > div:first-child {
    align-items: center;
    display: flex;
  }

  p {
    overflow-wrap: anywhere;
    text-align: left;
  }
`;

interface stateProps {
  clickable: boolean;
}

const Result = styled.div`
  position: relative;
  width: 600px;
  padding: 0 10px;

  h3 {
    margin-top 10px;
  }

  @media screen and (max-width: 980px) {
    width: 420px;
  }
`;

const Theme = styled.div`
  margin: 0 auto;
  margin-bottom: 20px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  position: relative;

  > div {
    border: solid 1px #ced4da;
    border-top: none;
    padding: 4px 20px;
    padding-bottom: 20px;

    display: flex;
    justify-content: center;
    align-items: center;

    min-height: 110px;

    > span {
      position: absolute;
      bottom: 16px;
    }
  }

  p {
    text-align: center;
    font-size: 5.2rem;
    font-weight: bold;
    margin-bottom: 10px;
  }

  h2 {
    background: #f2a118;
    color: white;
    font-size: 2.6rem;
    font-weight: bold;
    text-align: center;
    padding-bottom: 4px;
  }
`;

export const Elements = styled.div`
  background: rgba(206, 212, 218, 0.25);
  padding: 10px;

  h2 {
    transition: 0.2s;
    margin-left: -10px;
  }

  > ul {
    > li {
      margin: 6px 6px;
      border: 1px solid #ced4da;
      padding: 8px 10px;
      list-style: none;
      text-align: center;
      font-size: 1.6rem;
    }
  }

  margin-bottom: 20px;
`;

const CheckListWrapper = styled.ul`
  display: flex;
  flex-wrap: wrap;
`;

const SingleCheckList = styled.li`
  display: block;
  list-style: none;
  width: calc(33% - 28px);
  background: #ffb943;
  color: white;
  margin: 5px;
  padding: 10px;
  padding-top: 20px;
  padding-bottom: 30px;
  text-align: center;
  transition: 0.2s;

  ${(p: stateProps) => {
    return p.clickable
      ? `
      cursor: pointer;
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
      transform: scale(1.02);
      
      &:hover {
        transform: scale(1.1);
        z-index:9;
      }
    `
      : `
      transform: scale(1);
    `;
  }}

  p {
    font-weight: bold;
    font-size: 5.2rem;
    margin-bottom: 10px;
  }

  > span {
    font-weight: bold;
    font-size: 1.6rem;
    white-space: pre-wrap;
  }

  &:nth-child(2n) {
    background: #f2a118;
  }

  @media screen and (max-width: 980px) {
    padding: 4px;
    padding-top: 6px;
    padding-bottom: 10px;

    p {
      font-size: 4rem;
    }

    > span {
      font-size: 1.3rem;
    }
  }
`;

const Wrap = styled.div`
  user-select: none;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  padding: 0 20px;
`;

const Wrapper = styled.div`
  display: flex;
  margin-top: 30px;
`;

const Main = styled.div`
  flex: 1;
  padding: 0 10px;
  display: flex;
  justify-content: center;
`;

const ThemeWrap = styled.div`
  flex: 1;
  padding-bottom: 20px;
`;

export const TitleElement: React.FC = () => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='296'
      height='40'
      viewBox='0 0 296 40'
    >
      <g transform='translate(58.605 -0.063)'>
        <g transform='translate(0 0)'>
          <g transform='translate(0)'>
            <path
              d='M0,0H242V40H0Z'
              transform='translate(-58.605 0.063)'
              fill='#343a40'
            />
            <rect
              width='20'
              height='40'
              transform='translate(190.395 0.063)'
              fill='#343a40'
            />
            <rect
              width='20'
              height='40'
              transform='translate(217.395 0.063)'
              fill='#343a40'
            />
            <path
              d='M-181.02-12.33V-20.7a5.55,5.55,0,0,1-4.8-4.71h-8.76a17.661,17.661,0,0,0,4.74,8.91h-10.41a17.061,17.061,0,0,0,4.89-8.91h-8.76c-.03,1.65-2.01,4.38-4.8,4.71v8.37a8.685,8.685,0,0,0,1.8-.24V-9h4.41c-.84,1.86-2.79,3.12-5.64,3.48v8.1c6.99-.12,12.51-5.31,14.16-11.58h3.81v2.85a.7.7,0,0,1-.81.81h-4.05V2.58h6.51c4.14,0,6.18-3.06,6.18-7.26v-7.83A8.867,8.867,0,0,0-181.02-12.33Zm29.97-4.92V-25.2h-13.29v5.58h1.71a1.245,1.245,0,0,1-1.23.72v5.37c2.91-.63,4.41-1.44,5.28-3.12v2.67h4.53A3.172,3.172,0,0,0-151.05-17.25Zm.03,18.51V-5.4h-1.23V-6.63h1.23v-6h-1.23v-.87h-6.27v.87h-.72a7.922,7.922,0,0,0,.06-.87h-4.68a27.7,27.7,0,0,1-.48,6.03V-18.36h-1.14l.48-1.14v-5.34h-5.82a3.306,3.306,0,0,1,.06-.54h-6.33c0,1.56-.69,3.39-1.89,4.02v8.67a3.733,3.733,0,0,0,.6-.3v2.85c0,1.23-.18,1.59-.6,1.83V2.67c3.33-.96,5.7-4.05,6.6-6.9h1.62v.24c0,.42-.21.54-.54.54h-1.02V2.58h3.21c3.09,0,4.77-1.89,4.77-5.91V-5.94h4.77a3.676,3.676,0,0,0,.27-.69h.78V-5.4h-5.64V1.26h4.11V2.61h7.8V1.26Zm-5.82-20.64c0,.27-.09.51-.42.51h-.54q.135-.54.27-1.26h.69Zm-14.25,0-.48,1.02h-1.35c.18-.33.36-.66.54-1.02Zm1.8,6.12h-1.02v-.96h1.02Zm-3,0h-.99v-.96h.99Zm3,4.89h-1.02v-.87h1.02Zm-3,0h-.99v-.87h.99Zm51.27,11.25V-3.24l-3.27-.33c.15-.24.3-.48.45-.75h2.82v-6.3h-11.67a4.25,4.25,0,0,0,.24-.54h10.74v-7.02h-4.32v-.51h4.95v-6.3H-149.1l.09,6.3h5.04v.51h-4.41v7.02h6.72l-.27.54h-7.05v6.3h2.46a7.725,7.725,0,0,1-1.11,1.11l3.09.78c-1.32.03-2.79.03-4.44.03V2.82c7.68,0,13.2-.48,17.19-1.62Zm-7.65-17.04h-1.26v-.96h1.26Zm-5.7-4.02h-1.35v-.78h1.35Zm0,4.02h-1.35v-.96h1.35Zm-5.73,0h-1.32v-.96h1.32Zm7.71,9.51a5.935,5.935,0,0,1-1.29.81l-3.06-.3a4.4,4.4,0,0,0,.45-.51Zm41.7,2.52-1.5-8.4h-3.18l1.41-.87h2.88v-4.41h-9.69v-.54h9.12v-3.54h-9.12v-.51h9.69v-4.35h-9.69v-.63h-8.37v.63h-9.87v4.35h9.87v.51h-9.27v3.54h9.27v.54h-9.87v4.41h1.8l-1.5,1.02v2.82h4.92l-.99.54h-4.14V-2.1h9.78V2.61h8.55V-2.4a8.258,8.258,0,0,0,2.01-.42l.12.69Zm-.21,4.74-.99-4.32h-8.37l1.17,4.32Zm-18.93-4.2h-8.58a39.8,39.8,0,0,1-.78,4.2h8.7A26.145,26.145,0,0,0-109.83-1.59ZM-99.39-7.5a15.341,15.341,0,0,1-3.45.42l3.03-1.71h.18Zm-6.9-3.9-1.5.84h-2.22l1.35-.84Zm44.1-2.13V-24.21h-8.85v10.53c0,5.1-1.74,6.81-6.15,6.81h-7.44V1.86h7.71C-67.53,1.86-62.19-1.35-62.19-13.53ZM-79.08-8.88V-24.21H-87.9V-8.88ZM-31.14,1.8V-6.93a15.61,15.61,0,0,1-5.88-.96,17.856,17.856,0,0,0,2.64-9.18v-6.6H-57.84v7.98h14.22v.63c0,2.46-3.33,8.31-15.18,8.31V2.07c6.63,0,11.61-1.47,15.33-3.66C-40.08.75-37.89,1.8-31.14,1.8ZM-2.73-5.64v-8.82H-6.48c-6.9,0-11.58-.99-11.58-3.84v-6.24h-9.21V1.86h9.21V-7.2A36,36,0,0,0-6.48-5.64Z'
              transform='translate(172.395 32.063)'
              fill='#fff'
            />
          </g>
        </g>
      </g>
    </svg>
  );
};

const TitleCheck: React.FC = () => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='296'
      height='40'
      viewBox='0 0 296 40'
    >
      <g transform='translate(58.605 -0.063)'>
        <path
          d='M0,0H242V40H0Z'
          transform='translate(-58.605 0.063)'
          fill='#343a40'
        />
        <rect
          width='20'
          height='40'
          transform='translate(190.395 0.063)'
          fill='#343a40'
        />
        <rect
          width='20'
          height='40'
          transform='translate(217.395 0.063)'
          fill='#343a40'
        />
        <path
          d='M-181.38-7.92V-14.7h-2.97v-4.62a8.5,8.5,0,0,0,1.38-5.1h-9.99c0,.96-.6,1.44-2.31,1.44h-12.66v7.08h13.08c.45,0,.93-.03,1.38-.03v1.23h-15.21v6.78h14.79c-.75,1.02-2.61,1.74-6.18,1.74h-7.86V1.95h7.77c9.18,0,14.73-3.72,15.66-9.87Zm26.91,9.6V-5.04h-6.9V-9a4.741,4.741,0,0,0-.6-2.82h7.35v-6.66h-21.21v6.66h6.15c.99.27,1.11.9,1.11,3.33v3.45h-7.47V1.68Zm31.17-13.56v-7.59h-6.99v7.59a7.224,7.224,0,0,1-.66,3.27l-.33-3.33c-.42-4.23-2.28-7.14-6.57-7.14h-.87v5.1c-.78-3.15-2.7-5.1-6.27-5.1h-.87v6.87h.36c.6,0,.99.33.99,1.23v2.52h6.27l-.18-3.48v-.27h.06c.6,0,.9.33.96,1.23l.15,2.52h6.21c-1.53,2.85-5.28,3.69-10.74,3.69h-3.69V1.98h3.69C-130.11,1.98-123.3-2.61-123.3-11.88Zm31.92-1.14V-23.91h-22.74v.66a4.518,4.518,0,0,1-4.26,4.44V-9.9a12.021,12.021,0,0,0,10.26-5.58h7.95v2.46c0,3.48-1.5,6.39-9.21,6.39h-7.83V1.95h7.77C-94.47,1.95-91.38-5.34-91.38-13.02Zm29.19-.51V-24.21h-8.85v10.53c0,5.1-1.74,6.81-6.15,6.81h-7.44V1.86h7.71C-67.53,1.86-62.19-1.35-62.19-13.53ZM-79.08-8.88V-24.21H-87.9V-8.88ZM-31.14,1.8V-6.93a15.61,15.61,0,0,1-5.88-.96,17.856,17.856,0,0,0,2.64-9.18v-6.6H-57.84v7.98h14.22v.63c0,2.46-3.33,8.31-15.18,8.31V2.07c6.63,0,11.61-1.47,15.33-3.66C-40.08.75-37.89,1.8-31.14,1.8ZM-2.73-5.64v-8.82H-6.48c-6.9,0-11.58-.99-11.58-3.84v-6.24h-9.21V1.86h9.21V-7.2A36,36,0,0,0-6.48-5.64Z'
          transform='translate(172.395 32.063)'
          fill='#fff'
        />
      </g>
    </svg>
  );
};

export const Checked: React.FC = () => {
  return (
    <svg
      id='Component_-_Complete'
      data-name='Component - Complete'
      xmlns='http://www.w3.org/2000/svg'
      width='20'
      height='20'
      viewBox='0 0 20 20'
    >
      <path
        id='パス_206'
        data-name='パス 206'
        d='M10,0A10,10,0,1,1,0,10,10,10,0,0,1,10,0Z'
        fill='#ffa000'
      />
      <path
        id='Checkbox'
        d='M4.6,8,0,3.4l.933-.933L4.6,6.067,10.667,0,11.6.933Z'
        transform='translate(4.001 6)'
        fill='#fff'
      />
    </svg>
  );
};

export default Stage2Answer;
